import cronstrue from 'cronstrue';

/**
 * Human-readable label for a cron schedule. Hybrid by design: a curated set of
 * common shapes gets the app's natural phrasing ("Every day at 9:00 AM",
 * "On the 1st of every month at 2:30 AM"), and anything outside that set falls
 * back to cronstrue so even hand-written custom expressions read as a sentence
 * rather than a raw `15 3 * 1,6 2`.
 */
export function cronScheduleLabel(cron: string): string {
  const common = commonCronPhrase(cron);
  if (common !== null) {
    return common;
  }
  try {
    return cronstrue.toString(cron, {
      throwExceptionOnParseError: true,
      verbose: false,
      use24HourTimeFormat: false,
    });
  } catch {
    // Last resort for an expression neither path understands.
    return cron.trim();
  }
}

/**
 * The full display label for a cron trigger: the schedule phrase plus its
 * timezone, always shown so two triggers that differ only by timezone (9:00 AM
 * UTC vs 9:00 AM New York) never render the same label.
 */
export function cronTriggerLabel(params: {
  cron: string;
  timezone: string;
}): string {
  const tz = params.timezone.trim();
  return `${cronScheduleLabel(params.cron)} (${tz === '' ? 'UTC' : tz})`;
}

/** Display label for an internal event trigger. */
export function eventTriggerLabel(eventName: string): string {
  return `Event: ${eventName.trim()}`;
}

/**
 * Display label for a webhook trigger: "{Provider} · {Event}" plus a `target`
 * summary of the trigger's identifying config when it has one (GitHub → the
 * repo, e.g. `cotera/app`). `providerLabel` and `target` are resolved by the
 * caller — the server derives `target` from the provider's `projectIdentity`, so
 * whatever distinguishes two triggers for dedup is exactly what shows in the
 * label. The SDK stays provider-agnostic.
 */
export function webhookTriggerLabel(params: {
  providerLabel: string;
  eventLabel: string;
  target?: string;
}): string {
  const base = `${params.providerLabel} · ${params.eventLabel}`;
  const target = params.target?.trim();
  return target === undefined || target === '' ? base : `${base} · ${target}`;
}

/**
 * Fallback humanization of a raw event key, for when no curated display name is
 * available (`pull_request.opened` → "Pull request opened"). Callers prefer the
 * provider manifest's event display name and use this only as a backstop.
 */
export function humanizeEventKey(eventKey: string): string {
  const words = eventKey
    .trim()
    .replace(/[._/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (words === '') {
    return 'Event';
  }
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const DAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

function formatTime(hour: number, minute: number): string {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
}

function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return `${n}th`;
  }
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** A literal integer cron field (e.g. `9`), or null for `*`, steps, lists, ranges. */
function cronInt(field: string | undefined): number | null {
  if (field === undefined || !/^\d+$/.test(field)) {
    return null;
  }
  const n = parseInt(field, 10);
  return Number.isNaN(n) ? null : n;
}

/** The N in a star-slash-N step field (e.g. every-2 -> 2), or null if not a step. */
function cronStep(field: string | undefined): number | null {
  if (field === undefined) {
    return null;
  }
  const match = /^\*\/(\d+)$/.exec(field);
  if (match === null) {
    return null;
  }
  const n = parseInt(match[1] ?? '', 10);
  return Number.isNaN(n) || n <= 0 ? null : n;
}

/**
 * Natural phrasing for the common schedule shapes, or null when the expression
 * is outside the curated set (so the caller falls back to cronstrue). Extends
 * the client's schedule-picker phrasing with arbitrary minutes and a
 * day-of-month case; server- and client-derived labels stay identical.
 */
function commonCronPhrase(cron: string): string | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) {
    return null;
  }
  const [minute, hourStr, dayOfMonth, month, dowStr] = parts;
  const everyDay = dayOfMonth === '*' && month === '*' && dowStr === '*';

  if (minute === '*' && hourStr === '*' && everyDay) {
    return 'Every minute';
  }

  const minuteStep = cronStep(minute);
  if (minuteStep !== null && hourStr === '*' && everyDay) {
    return minuteStep === 1 ? 'Every minute' : `Every ${minuteStep} minutes`;
  }

  const minuteInt = cronInt(minute);

  if (minuteInt !== null && hourStr === '*' && everyDay) {
    return minuteInt === 0
      ? 'Every hour'
      : `Every hour at :${String(minuteInt).padStart(2, '0')}`;
  }

  const hourStep = cronStep(hourStr);
  if (minuteInt === 0 && hourStep !== null && everyDay) {
    return hourStep === 1 ? 'Every hour' : `Every ${hourStep} hours`;
  }

  const h = cronInt(hourStr);

  // Everything below is "at a specific time of day"; bail if either is not a
  // plain literal (ranges/lists/steps go to cronstrue).
  if (minuteInt === null || h === null) {
    return null;
  }
  const time = formatTime(h, minuteInt);

  if (everyDay) {
    return `Every day at ${time}`;
  }

  const dow = cronInt(dowStr);
  if (dayOfMonth === '*' && month === '*') {
    if (dow !== null && DAY_LABELS[dow] !== undefined) {
      return `Every ${DAY_LABELS[dow]} at ${time}`;
    }
    if (dowStr === '1-5') {
      return `Weekdays at ${time}`;
    }
    if (dowStr === '0,6' || dowStr === '6,0' || dowStr === '6,7') {
      return `Weekends at ${time}`;
    }
  }

  const dom = cronInt(dayOfMonth);
  if (dom !== null && month === '*' && dowStr === '*') {
    return `On the ${ordinal(dom)} of every month at ${time}`;
  }

  return null;
}
